import { useState, useEffect } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/config/firebase.config';
import { signIn, signUp, signOut, getAuthErrorMessage } from '@/services';
import { UserProfile } from '@/types';
import { getUserProfile, createUserProfile } from '@/services';

export interface UseAuthReturn {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string, postalCode?: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  refreshUserProfile: () => Promise<void>;
}

/**
 * Custom hook for authentication
 */
export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        try {
          let profile = await getUserProfile(firebaseUser.uid);

          // If profile doesn't exist, create it
          if (!profile) {
            console.log('Profile not found, creating...');
            await createUserProfile({
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || undefined,
              language: 'en',
            });
            profile = await getUserProfile(firebaseUser.uid);
          }

          setUserProfile(profile);
        } catch (err) {
          console.error('Error loading/creating user profile:', err);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const handleSignIn = async (email: string, password: string): Promise<void> => {
    try {
      setError(null);
      setLoading(true);
      await signIn(email, password);
    } catch (err) {
      setError(getAuthErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (
    email: string,
    password: string,
    displayName?: string,
    postalCode?: string
  ): Promise<void> => {
    try {
      setError(null);
      setLoading(true);
      await signUp(email, password, { displayName, postalCode });
    } catch (err) {
      setError(getAuthErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      setError(null);
      await signOut();
    } catch (err) {
      setError(getAuthErrorMessage(err));
      throw err;
    }
  };

  const clearError = (): void => {
    setError(null);
  };

  const refreshUserProfile = async (): Promise<void> => {
    if (!user) return;

    try {
      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);
    } catch (err) {
      console.error('Error refreshing user profile:', err);
    }
  };

  return {
    user,
    userProfile,
    loading,
    error,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signOut: handleSignOut,
    clearError,
    refreshUserProfile,
  };
};
